import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, X as XIcon, Inbox, FolderKanban, Clock, Briefcase, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function MyProjects() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [declineFor, setDeclineFor] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/me/project-invitations");
      setItems(data || []);
    } catch { toast.error("Could not load projects"); }
  };
  useEffect(() => { load(); }, []);

  const pending = items.filter(i => i.status === "Pending");
  const active = items.filter(i => i.status === "Active");

  const accept = async (m) => {
    setBusy(m.id);
    try {
      await api.post(`/projects/${m.project_id}/invitation/accept`);
      toast.success("Project accepted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept");
    } finally { setBusy(null); }
  };

  const submitDecline = async () => {
    if (!declineFor) return;
    setBusy(declineFor.id);
    try {
      await api.post(`/projects/${declineFor.project_id}/invitation/decline`, { reason });
      toast.success("Invitation declined");
      setDeclineFor(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not decline");
    } finally { setBusy(null); }
  };

  return (
    <AppLayout title="My Projects" subtitle="Invitations & active engagements">
      <Tabs defaultValue={pending.length > 0 ? "pending" : "active"}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            <Inbox className="w-3.5 h-3.5 mr-1.5"/>Pending
            {pending.length > 0 && <span className="ml-1.5 pill bg-amber-50 text-amber-800 border-amber-300">{pending.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">
            <FolderKanban className="w-3.5 h-3.5 mr-1.5"/>Active
            {active.length > 0 && <span className="ml-1.5 pill bg-emerald-50 text-emerald-700 border-emerald-300">{active.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-5">
          {pending.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="No pending invitations"
              hint="You're all caught up. Admins will message you when a new project needs your expertise."
              testid="empty-pending"
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pending-list">
              {pending.map(m => (
                <PendingCard
                  key={m.id}
                  m={m}
                  busy={busy === m.id}
                  onAccept={() => accept(m)}
                  onDecline={() => { setDeclineFor(m); setReason(""); }}
                  onPreview={() => nav(`/projects/${m.project_id}`)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="active" className="mt-5">
          {active.length === 0 ? (
            <EmptyState
              icon={FolderKanban}
              title="No active projects yet"
              hint="Once you accept an invitation, your project will land here."
              testid="empty-active"
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="active-list">
              {active.map(m => (
                <ActiveCard key={m.id} m={m} onOpen={() => nav(`/projects/${m.project_id}`)} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Decline modal */}
      <Dialog open={!!declineFor} onOpenChange={(o) => { if (!o) setDeclineFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline invitation</DialogTitle>
            <DialogDescription>
              Optionally let the team know why you&apos;re declining {declineFor?.project_name ? <b>&ldquo;{declineFor.project_name}&rdquo;</b> : "this project"}. This helps them route future invitations better.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Reason (optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={280}
              rows={3}
              placeholder="e.g. unavailable that week, scope outside my expertise…"
              data-testid="input-decline-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineFor(null)} data-testid="btn-decline-cancel">Cancel</Button>
            <Button
              onClick={submitDecline}
              disabled={busy === declineFor?.id}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btn-decline-confirm"
            >
              <XIcon className="w-3.5 h-3.5 mr-1.5"/>Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function PendingCard({ m, busy, onAccept, onDecline, onPreview }) {
  return (
    <div className="kpi-card border-l-4 border-l-amber-500 flex flex-col" data-testid={`card-pending-${m.project_id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <Briefcase className="w-3 h-3"/>{m.client_name || "Project"}
        </div>
        <span className="pill bg-amber-50 text-amber-800 border-amber-300 text-xs inline-flex items-center gap-1">
          <Clock className="w-3 h-3"/>Pending
        </span>
      </div>
      <h3 className="font-display font-bold text-slate-900 leading-tight" data-testid={`pending-title-${m.project_id}`}>{m.project_name}</h3>
      <p className="text-sm text-slate-600 mt-2 line-clamp-3 flex-1">{m.project_description || "No description provided."}</p>
      <div className="text-xs text-slate-500 mt-3">Invited {m.invited_at ? new Date(m.invited_at).toLocaleDateString() : "—"}</div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          onClick={onDecline}
          disabled={busy}
          data-testid={`btn-decline-${m.project_id}`}
        >
          <XIcon className="w-3.5 h-3.5 mr-1.5"/>Decline
        </Button>
        <Button
          onClick={onAccept}
          disabled={busy}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid={`btn-accept-${m.project_id}`}
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5"/>Accept
        </Button>
      </div>
      <button
        type="button"
        onClick={onPreview}
        className="text-xs text-slate-500 hover:text-cyan-700 mt-3 text-left underline-offset-2 hover:underline"
        data-testid={`btn-preview-${m.project_id}`}
      >
        Preview project details →
      </button>
    </div>
  );
}

function ActiveCard({ m, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="kpi-card text-left hover:border-slate-300 transition-all"
      data-testid={`card-active-${m.project_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <Briefcase className="w-3 h-3"/>{m.client_name || "Project"}
        </div>
        <span className="pill bg-emerald-50 text-emerald-700 border-emerald-300 text-xs inline-flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3"/>Active
        </span>
      </div>
      <h3 className="font-display font-bold text-slate-900 leading-tight">{m.project_name}</h3>
      <p className="text-sm text-slate-600 mt-2 line-clamp-2">{m.project_description || "—"}</p>
      <div className="flex items-center justify-between text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">
        <span>{m.open_tasks} open task{m.open_tasks === 1 ? "" : "s"}</span>
        <span className="inline-flex items-center gap-1 text-cyan-700">Open <ArrowRight className="w-3 h-3"/></span>
      </div>
    </button>
  );
}

function EmptyState({ icon: Icon, title, hint, testid }) {
  return (
    <div className="kpi-card text-center py-12" data-testid={testid}>
      <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mx-auto mb-3">
        <Icon className="w-5 h-5"/>
      </div>
      <h3 className="font-display font-bold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 mt-1.5 max-w-sm mx-auto">{hint}</p>
    </div>
  );
}
